'use client'

import { ExternalLink } from 'lucide-react'

import { parseTransactionError, formatForSupport, getActionText } from '@/lib/errors'
import { CopyButton } from '@/ui/atoms/copy-button/CopyButton'
import { links } from '@/ui/constants/links'
import { cn } from '@/ui/utils/style'

interface TransactionErrorPanelProps {
  error: Error | string | null | undefined
  showSimulationWarning?: boolean
  displayMessage?: string
  className?: string
}

export function TransactionErrorPanel({
  error,
  showSimulationWarning,
  displayMessage,
  className,
}: TransactionErrorPanelProps) {
  if (!error) return null

  if (typeof error === 'string') {
    return (
      <div className={cn('mt-4 rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-red-400 text-sm', className)}>
        <p>{displayMessage ?? error}</p>
        {showSimulationWarning && (
          <p className="mt-2 text-xs opacity-75">
            The transaction simulation failed. This usually means the transaction would revert on-chain.
          </p>
        )}
        <div className="mt-3 flex items-center gap-3">
          <a
            href={links.social.discord}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-red-400/80 text-xs underline transition-colors hover:text-red-300"
          >
            <ExternalLink size={12} />
            Contact support
          </a>
          <CopyButton text={error} iconSize={12} showText className="text-red-400/80 hover:text-red-300" />
        </div>
      </div>
    )
  }

  const parsed = parseTransactionError(error)

  if (parsed.shouldHide) {
    return null
  }

  const errorMessage = displayMessage ?? parsed.userMessage
  const actionHint = getActionText(parsed.suggestedAction)
  const copyText = formatForSupport(parsed)

  const borderColor = parsed.severity === 'warning' ? 'border-yellow-500/20' : 'border-red-500/20'
  const bgColor = parsed.severity === 'warning' ? 'bg-yellow-500/10' : 'bg-red-500/10'
  const textColor = parsed.severity === 'warning' ? 'text-yellow-400' : 'text-red-400'
  const linkColor =
    parsed.severity === 'warning' ? 'text-yellow-400/80 hover:text-yellow-300' : 'text-red-400/80 hover:text-red-300'

  return (
    <div className={cn('mt-4 rounded-lg border p-3 text-sm', borderColor, bgColor, textColor, className)}>
      <p>{errorMessage}</p>

      {actionHint && parsed.suggestedAction !== 'none' && parsed.suggestedAction !== 'retry' && (
        <p className="mt-1.5 text-xs opacity-75">{actionHint}</p>
      )}

      {showSimulationWarning && (
        <p className="mt-2 text-xs opacity-75">
          The transaction simulation failed. This usually means the transaction would revert on-chain.
        </p>
      )}

      <div className="mt-3 flex items-center gap-3">
        <a
          href={links.social.discord}
          target="_blank"
          rel="noopener noreferrer"
          className={cn('inline-flex items-center gap-1 text-xs underline transition-colors', linkColor)}
        >
          <ExternalLink size={12} />
          Contact support
        </a>
        <CopyButton text={copyText} iconSize={12} showText className={linkColor} />
      </div>
    </div>
  )
}
